import {
  computed,
  inject,
  provide,
  toValue,
  type ComputedRef,
  type InjectionKey,
  type MaybeRefOrGetter,
} from 'vue';
import type { ContractGenerationTemplateFieldMetadata } from '@/api';
import type { TemplateFieldMetadataMap } from '@/types/contractGeneration/wizard';
import { lookupFieldMetadata } from '@/utils/contractGeneration/templateFieldLabels';

/**
 * The wizard's authored field metadata travels from the answer panel to every
 * field beneath it by injection rather than four levels of props. Key and both
 * halves live here so the type travels with the key. With nothing provided the
 * consumers see `null` and render exactly as the wizard always has.
 */
const TEMPLATE_FIELD_METADATA_KEY: InjectionKey<ComputedRef<TemplateFieldMetadataMap | null>> =
  Symbol('cg:field-metadata');

/** Called once by the answer panel; every field component below it reads through the hooks. */
export function provideTemplateFieldMetadata(
  source: MaybeRefOrGetter<TemplateFieldMetadataMap | null | undefined>
): void {
  provide(
    TEMPLATE_FIELD_METADATA_KEY,
    computed(() => toValue(source) ?? null)
  );
}

/** The whole map — what a container needs to order its children. */
export function useTemplateFieldMetadataMap(): ComputedRef<TemplateFieldMetadataMap | null> {
  const injected = inject(TEMPLATE_FIELD_METADATA_KEY, null);
  return computed(() => injected?.value ?? null);
}

/** One field's entry by its runtime path — list indexes are normalized away inside. */
export function useTemplateFieldMetadata(
  runtimePath: MaybeRefOrGetter<string>
): ComputedRef<ContractGenerationTemplateFieldMetadata | null> {
  const map = useTemplateFieldMetadataMap();
  return computed(() => lookupFieldMetadata(map.value, toValue(runtimePath)));
}
